import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import Modal, { toggleModalByName } from 'src/universal/components/Modal';
import { createDtmHandler } from 'src/universal/utils/analyticsTracking';
import {
  Color,
  FontType,
  Type,
  Align,
  Breakpoint,
  Link,
  Button,
  Typography,
  Row,
  Column
} from '@americanexpress/aqx-components';
import textRemoveTags from 'service_modules/utils/text-remove-tags';
import AmazonDisclaimer from 'page_modules/business-vac/components/AmazonDisclaimer';

import styles from './__styles__/index.scss?modules';

/**
 * ApplyModal - modal component used for currently for amazon cards
 * @param {object} data - {header, description, applyButtonm, cancelButton}
 * @param {node} children - React Children
 * @param {string} qeId - qeId used for E2E testing
 * @param {object[strings]} styles - styles object to pass in
 */
const ApplyModal = React.memo(({ data, modalName = 'APPLY' }) => {
  const { header, description, applyButton, cancelButton, cardHeadline } =
    data || {};
  const onCancelClick = useCallback(
    () => {
      createDtmHandler({ dtmObj: cancelButton.dtm })();
      toggleModalByName(modalName);
    },
    [cancelButton]
  );
  const onApplyClick = useCallback(
    () => createDtmHandler({ dtmObj: applyButton.dtm })(),
    []
  );

  const ariaVal = cardHeadline ? `Dialog for ${cardHeadline}` : 'Dialog';

  return (
    <Modal name={modalName} qeId="applyModal" ariaLabel={ariaVal} styles={styles}>
      <div className={styles.applyModalContainer} role="alert">
        {header && (
          <Row $sm="center">
            <Breakpoint>
              {({ sm } = {}) => (
                <Typography
                  theme={{
                    color: Color.Gray05,
                    marginBottom: sm ? 20 : 30,
                    textAlign: Align.Center,
                    horizontallyFluid: true,
                    textHeading: 4,
                    fontType: FontType.BentonMedium
                  }}
                  aria-label={`${textRemoveTags(header)} ${textRemoveTags(
                    description
                  )}`}
                >
                  {header}
                </Typography>
              )}
            </Breakpoint>
          </Row>
        )}
        {description && (
          <Row>
            <Breakpoint>
              {({ sm } = {}) => (
                <Typography
                  theme={{
                    color: Color.Gray05,
                    marginBottom: sm ? 20 : 30,
                    textAlign: Align.Center,
                    horizontallyFluid: true,
                    textBody: 3
                  }}
                >
                  {description}
                </Typography>
              )}
            </Breakpoint>
          </Row>
        )}
        <AmazonDisclaimer isAmazonModal={true} isPDPModal={true} />
        <Row>
          {cancelButton && (
            <Column $md="6" $sm="12">
              <Breakpoint>
                {({ sm } = {}) => (
                  <Button
                    qeId="modal-cancel-button"
                    aria-label="cancel"
                    $type={Type.Secondary}
                    theme={{ horizontallyFluid: true, marginTop: sm ? 15 : 0 }}
                    title={cancelButton.text}
                    onClick={onCancelClick}
                  >
                    {cancelButton.text || ''}
                  </Button>
                )}
              </Breakpoint>
            </Column>
          )}
          {applyButton && (
            <Column $md="6" $sm="12" $smPosition="first" $mdPosition="last">
              <Link
                href={applyButton.url || ''}
                tabIndex="-1"
                theme={{ horizontallyFluid: true }}
              >
                <Button
                  qeId="modal-apply-button"
                  title={applyButton.text}
                  id={applyButton.id}
                  onClick={onApplyClick}
                  theme={{ horizontallyFluid: true }}
                >
                  {applyButton.text || ''}
                </Button>
              </Link>
            </Column>
          )}
        </Row>
      </div>
    </Modal>
  );
});

ApplyModal.propTypes = {
  data: PropTypes.shape({
    header: PropTypes.string,
    description: PropTypes.string,
    applyButton: PropTypes.shape({
      dtm: PropTypes.object,
      url: PropTypes.string,
      text: PropTypes.string
    }),
    cancelButton: PropTypes.shape({
      dtm: PropTypes.object,
      url: PropTypes.string,
      text: PropTypes.string
    })
  }),
  modalName: PropTypes.object
};

export default ApplyModal;
